import classNames from 'classnames'
import { useState } from 'react'

import Card from '@/components/Card/Card'
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import { DeckType } from '@/types/deck'
import { bulkExport, formatUrl } from '@/utils/helpers'

import styles from './Deck.module.scss'
import DeckCategory from './DeckCategory'

const categoryRE = /\[([A-zÀ-ž-\s\d']+)\]/g
const cardLineRE = /\*?(\d+)\s+([A-zÀ-ž\s'-\/|\d:]+)/

const fixCategory = (category: string) => category.replace('[', '').replace(']', '')

const parseDeck = (rawDeck: string) => {
  const categories = rawDeck.match(categoryRE) as RegExpMatchArray
  const deckLines = rawDeck.split('\n')

  const deck: DeckType = {}
  const bulkExportLines: string[] = []
  let latestCategory = ''
  let numCards = 0
  let columnOne: string[] = []

  deckLines.forEach((line) => {
    if (categories?.includes(line)) {
      latestCategory = fixCategory(line)
    } else if (line.length > 3) {
      if (line.slice(0, 2) !== '[/') {
        const cardParts = cardLineRE.exec(line)
        const card = {
          cardcount: cardParts ? cardParts[1] : '1',
          cardname: cardParts ? cardParts[2] : line,
        }

        if (!deck[latestCategory]) deck[latestCategory] = []
        deck[latestCategory].push(card)
        bulkExportLines.push(`${card.cardcount} ${card.cardname}`)
        numCards += 1
      } else {
        if (numCards >= (deckLines.length - (categories?.length || 0) * 2) / 2 && columnOne.length === 0) {
          columnOne = Object.keys(deck)
        }
      }
    }
  })

  const firstCard = categories && deck[fixCategory(categories[0])] && deck[fixCategory(categories[0])][0].cardname
  const columnTwo = Object.keys(deck).slice(columnOne.length)

  return {
    ckLink: bulkExport({
      deckLines: bulkExportLines,
      medium: 'articles',
      partner: 'edhreccontent',
      vendor: 'cardkingdom',
    }),
    columns: [columnOne, columnTwo],
    deck,
    firstCard,
    tcgpLink: bulkExport({ deckLines: bulkExportLines, medium: 'articles', vendor: 'tcgplayer' }),
  }
}

type Props = {
  archidekt_link?: string
  rawDeck: string
  slug: string
  title: string
}

const Deck = ({ archidekt_link, rawDeck, slug, title }: Props) => {
  const { ckLink, columns, deck, firstCard, tcgpLink } = parseDeck(rawDeck)
  const [currentImage, setCurrentImage] = useState(firstCard)

  return (
    <div>
      <div
        dangerouslySetInnerHTML={{
          __html: `<!-- ${JSON.stringify(deck)} -->`,
        }}
        id='deck_for_archidekt_scraper'
      />
      <div className='align-items-center d-flex flex-column justify-content-center w-100'>
        <hr className='w-100' />
        <div className='align-items-center d-flex flex-column flex-md-row gap-2 justify-content-around w-100'>
          <h4 className='m-0'>{title}</h4>
          <OutboundLink
            className={classNames('btn', styles.archidekt)}
            url={
              archidekt_link
                ? archidekt_link
                : `https://archidekt.com/articles/edhrec/${slug}/${formatUrl(title.replace('-', ''))}`
            }
          >
            View on Archidekt
          </OutboundLink>
        </div>
        <hr className='w-100' />
        <div className={styles.columns}>
          {columns.map((column, i) => (
            <div key={i}>
              {column.map((categoryHeader, j) => (
                <DeckCategory
                  cards={deck[categoryHeader]}
                  header={categoryHeader}
                  key={`${i}-${j}`}
                  setCurrentImage={setCurrentImage}
                />
              ))}
            </div>
          ))}
          <div className={styles.cardColumn}>
            <Card name={currentImage} />
          </div>
        </div>
      </div>
      <div className='align-items-start d-flex flex-column w-100'>
        <OutboundLink className='btn btn-primary mb-2' url={ckLink}>
          Buy this decklist from Card Kingdom
        </OutboundLink>
        <OutboundLink className='btn btn-primary mb-2' disableUtm url={tcgpLink}>
          Buy this decklist on TCGplayer
        </OutboundLink>
      </div>
    </div>
  )
}

const DeckWithBoundary = (props: Props) => (
  <ErrorBoundary tag='Deck'>
    <Deck {...props} />
  </ErrorBoundary>
)

export default DeckWithBoundary
